// Fêtes & campagnes : chargement, « fêtes du moment » et petits helpers.
//
// Une fête porte une couleur et des détails ; un don s'y range via
// fete_id / fete_detail_id (indépendamment de son motif). Les dates des fêtes
// viennent de l'API Hebcal (déjà utilisée par le Kiddouch) : les fêtes en
// cours / à venir / tout juste passées (± 30 jours) passent en tête de la
// saisie. Hebcal indisponible → simple ordre par défaut, rien ne casse.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { apiClient, getAuthToken, getImpersonatedOrgId } from "./api";

export const MOMENT_WINDOW_DAYS = 30;
const DAY = 86400000;

export const FETE_PALETTE = [
  "#DC2626", "#2563EB", "#15803D", "#7C3AED", "#CA8A04", "#92400E",
  "#DB2777", "#0891B2", "#EA580C", "#65A30D", "#0F766E", "#B8962E",
];

// ── Chargement (cache léger en mémoire, CLOISONNÉ par compte ET par
// synagogue : un autre compte dans le même onglet, ou le propriétaire qui
// passe d'une synagogue à l'autre, ne voit jamais les fêtes précédentes) ──
let _cache = { key: null, list: null };
let _inflight = { key: null, promise: null };
const orgKey = () => `${getAuthToken() || "session"}|${getImpersonatedOrgId() || "self"}`;

export async function fetchFetes({ force = false } = {}) {
  const key = orgKey();
  if (!force && _cache.key === key && _cache.list) return _cache.list;
  if (force || !_inflight.promise || _inflight.key !== key) {
    const promise = apiClient.get("/fetes")
      .then((r) => {
        const list = r.data?.fetes || [];
        if (orgKey() === key) _cache = { key, list }; // compte changé entre-temps : on n'écrit pas
        return list;
      })
      .finally(() => { if (_inflight.promise === promise) _inflight = { key: null, promise: null }; });
    _inflight = { key, promise };
  }
  return _inflight.promise;
}

export function invalidateFetes() {
  _cache = { key: null, list: null };
  _inflight = { key: null, promise: null };
}

export function useFetes() {
  const [fetes, setFetes] = useState(_cache.key === orgKey() ? _cache.list : null);
  const [error, setError] = useState(null);
  const reload = useCallback(async (force = true) => {
    try {
      const list = await fetchFetes({ force });
      setFetes(list);
      setError(null);
      return list;
    } catch (e) {
      setError(e);
      setFetes((f) => f || []);
      return [];
    }
  }, []);
  useEffect(() => { reload(false); }, [reload]);
  return { fetes, reload, error };
}

// ── Dates des fêtes (Hebcal) ────────────────────────────────────────────────
// Jour LOCAL (pas UTC) : à 0h30 à Paris, on est bien le nouveau jour.
const pad2 = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const utcDay = (iso) => new Date(`${iso}T00:00:00Z`);

async function fetchHebcalWindow(today) {
  const key = `terouma_hebcal_${isoDay(today)}`;
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) return JSON.parse(cached);
  } catch { /* stockage indisponible : on refait l'appel */ }
  const t = utcDay(isoDay(today));
  const start = new Date(+t - (MOMENT_WINDOW_DAYS + 15) * DAY).toISOString().slice(0, 10);
  const end = new Date(+t + (MOMENT_WINDOW_DAYS + 15) * DAY).toISOString().slice(0, 10);
  const res = await axios.get(
    `https://www.hebcal.com/hebcal/?v=1&cfg=json&maj=on&min=on&mod=off&nx=off&ss=off&mf=off&c=off&i=off&start=${start}&end=${end}`,
    { timeout: 8000 },
  );
  const items = (res.data?.items || [])
    .filter((it) => it.category === "holiday" && it.title && it.date)
    .map((it) => ({ title: it.title, date: it.date.slice(0, 10) }));
  try { sessionStorage.setItem(key, JSON.stringify(items)); } catch { /* idem */ }
  return items;
}

function datesForFete(fete, items) {
  const prefixes = fete.hebcal || [];
  if (!prefixes.length) return null;
  const days = items
    // Veilles et fêtes mineures homonymes exclues : « Rosh Hashana
    // LaBehemot » (1er Eloul), « Purim Katan », « Pesach Sheni »…
    .filter((it) => !/^(Erev|Leil)\b/i.test(it.title)
      && !/\b(LaBehemot|Katan|Sheni)\b/i.test(it.title)
      && prefixes.some((p) => it.title.startsWith(p)))
    .map((it) => it.date)
    .sort();
  if (!days.length) return null;
  return { start: days[0], end: days[days.length - 1] };
}

// Statut « du moment » d'une fête : rank 0 en cours, 1 à venir, 2 vient de
// passer, 3 campagne, 4 le reste de l'année.
export function momentOf(fete, items, today = new Date()) {
  if (fete.kind === "campagne") return { rank: 3, label: "campagne", kind: "campagne" };
  const range = datesForFete(fete, items || []);
  if (!range) return { rank: 4, label: "", kind: "later" };
  const t = utcDay(isoDay(today));
  const s = utcDay(range.start);
  const e = utcDay(range.end);
  if (t >= s && t <= e) return { rank: 0, label: "en cours", kind: "now", range };
  const toStart = Math.round((s - t) / DAY);
  const sinceEnd = Math.round((t - e) / DAY);
  if (toStart > 0 && toStart <= MOMENT_WINDOW_DAYS) {
    return { rank: 1, d: toStart, label: toStart === 1 ? "demain" : `dans ${toStart} j`, kind: "soon", range };
  }
  if (sinceEnd > 0 && sinceEnd <= MOMENT_WINDOW_DAYS) {
    return { rank: 2, d: sinceEnd, label: sinceEnd === 1 ? "hier" : `il y a ${sinceEnd} j`, kind: "past", range };
  }
  return { rank: 4, label: "", kind: "later", range };
}

export function useFeteMoments() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchHebcalWindow(new Date())
      .then((list) => { if (alive) setItems(list); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);
  return items; // null = en chargement, [] = indisponible
}

// Tri : fêtes du moment d'abord (en cours, à venir, passées), puis campagnes,
// puis le reste dans l'ordre de l'année.
export function orderFetes(fetes, items, { includeArchived = false } = {}) {
  return (fetes || [])
    .filter((f) => includeArchived || !f.archived)
    .map((f) => ({ fete: f, moment: momentOf(f, items) }))
    .sort((a, b) => a.moment.rank - b.moment.rank
      || (a.moment.d || 0) - (b.moment.d || 0)
      || (a.fete.sort_order || 0) - (b.fete.sort_order || 0));
}

// ── Petits helpers d'affichage ──────────────────────────────────────────────
export function feteById(fetes, id) {
  return (fetes || []).find((f) => f.id === id) || null;
}

export function detailLabel(fete, detailId) {
  if (!fete || !detailId) return null;
  return (fete.details || []).find((d) => d.id === detailId)?.label || null;
}

// Couleur translucide : "#2563EB" + "15" → fond léger d'une pastille.
export const tint = (hex, alpha = "15") => `${hex || "#78716C"}${alpha}`;
